<script setup lang="ts">
import { AlertCircle, ChevronDown, Loader2 } from 'lucide-vue-next';
import 'v-calendar/style.css';
import { DatePicker } from 'v-calendar';
import { watch } from 'vue';

import { ICON_SIZE_LG, ICON_SIZE_MD, ICON_SIZE_XL } from '@/composables/booking/booking-widget.constants';
import { useDateSelectionDropdown } from '@/composables/booking/useDateSelectionDropdown.ts';
import { useBookingWidgetContext } from '@/types/booking-widget-context';

import MobileModal from '../MobileModal.vue';

const {
  dropdown,
  isOpen,
  isMobile,
  selectedDate,
  displayText,
  mobileRows,
  calendarRef,
  mobileScrollContainer,
  disabledDates,
  openDropdown,
  closeDropdown,
  selectDate,
  handleDayClick,
  ensureVisibleMonthsLoaded,
  retryCalendarLoad,
} = useDateSelectionDropdown();

const context = useBookingWidgetContext();

watch(() => context.bookingWidget.selectedDate.value, (newDate) => {
  selectedDate.value = newDate ?? null;
});
</script>

<template>
  <div ref="dropdown" class="date-dropdown">
    <button
      class="dropdown-button"
      :aria-expanded="isOpen"
      aria-haspopup="dialog"
      @click="openDropdown"
    >
      <span class="date-text" :class="{ placeholder: !selectedDate }">
        {{ displayText || $t('booking.selectDate') }}
      </span>
      <ChevronDown :size="ICON_SIZE_MD" :class="{ rotate: isOpen && !isMobile }" aria-hidden="true" />
    </button>

    <!-- Desktop Dropdown -->
    <div v-if="!isMobile && isOpen" class="dropdown-menu">
      <div v-if="context.bookingWidget.bookingError.value" class="calendar-error">
        <AlertCircle :size="ICON_SIZE_LG" color="var(--yb-color-error)" aria-hidden="true" />
        <p>{{ $t(`booking.${context.bookingWidget.bookingError.value}`) }}</p>
        <button class="retry-button" @click="retryCalendarLoad">
          {{ $t('booking.errorRetry') }}
        </button>
      </div>

      <template v-else>
        <div class="calendar-wrapper">
          <div class="calendar-content" :class="{ 'is-loading': context.bookingWidget.isLoadingCalendar.value }">
            <DatePicker
              ref="calendarRef"
              :model-value="selectedDate"
              mode="date"
              :min-date="new Date()"
              :disabled-dates="disabledDates"
              borderless
              expanded
              trim-weeks
              columns="2"
              :step="1"
              is-required
              :locale="context.bookingWidget.currentLocale.value"
              @update:pages="ensureVisibleMonthsLoaded"
              @update:model-value="selectDate"
              @dayclick="handleDayClick"
            />
          </div>
          <div v-if="context.bookingWidget.isLoadingCalendar.value" class="loader-overlay" role="status" :aria-label="$t('common.loading')">
            <Loader2 :size="ICON_SIZE_XL" class="yb-spinner" aria-hidden="true" />
          </div>
        </div>
      </template>
    </div>

    <!-- Mobile Modal -->
    <MobileModal
      v-if="isMobile"
      :is-open="isOpen"
      :title="$t('booking.selectDate')"
      @close="closeDropdown"
    >
      <template #body>
        <div v-if="context.bookingWidget.bookingError.value" class="calendar-error-mobile">
          <AlertCircle :size="ICON_SIZE_LG" color="var(--yb-color-error)" aria-hidden="true" />
          <p>{{ $t(`booking.${context.bookingWidget.bookingError.value}`) }}</p>
          <button class="retry-button" @click="retryCalendarLoad">
            {{ $t('booking.errorRetry') }}
          </button>
        </div>

        <template v-else>
          <div ref="mobileScrollContainer" class="calendar-wrapper-mobile">
            <div class="calendar-content" :class="{ 'is-loading': context.bookingWidget.isLoadingCalendar.value }">
              <DatePicker
                ref="calendarRef"
                :model-value="selectedDate"
                mode="date"
                :min-date="new Date()"
                :disabled-dates="disabledDates"
                borderless
                expanded
                trim-weeks
                is-required
                :columns="1"
                :rows="mobileRows"
                disable-page-swipe
                :locale="context.bookingWidget.currentLocale.value"
                @update:model-value="selectDate"
                @dayclick="handleDayClick"
              />
            </div>
            <div v-if="context.bookingWidget.isLoadingCalendar.value" class="loader-overlay-mobile" role="status" :aria-label="$t('common.loading')">
              <Loader2 :size="ICON_SIZE_XL" class="yb-spinner" aria-hidden="true" />
            </div>
          </div>
        </template>
      </template>
    </MobileModal>
  </div>
</template>

<style scoped lang="css">
.date-dropdown {
  position: relative;
  width: 100%;
  min-width: 0;
}

.dropdown-button {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  min-width: 0;
  min-height: 74px;
  max-height: 74px;
  padding: var(--yb-spacing-sm) var(--yb-spacing-base);
  background-color: var(--yb-color-white);
  color: var(--yb-color-black);
  border: 1px solid var(--yb-color-mediumgray);
  border-radius: 0 var(--yb-radius-sm) var(--yb-radius-sm) 0;
  cursor: pointer;
  font-size: var(--yb-text-sm);

  &:disabled {
    cursor: not-allowed;
    opacity: 0.6;
  }

  .date-text {
    font-size: var(--yb-text-sm);
    color: var(--yb-color-black);
    text-align: left;

    &.placeholder {
      color: var(--yb-color-mediumgray);
    }
  }

  svg {
    flex-shrink: 0;
    transition: transform 0.2s ease-out;

    &.rotate {
      transform: rotate(180deg);
    }
  }
}

.dropdown-menu {
  position: absolute;
  top: calc(100% + 8px);
  right: 0;
  width: fit-content;
  min-width: 100%;
  background-color: var(--yb-color-white);
  border-radius: var(--yb-radius-sm);
  box-shadow: var(--yb-color-mediumgray) 0 4px 8px 0px;
  z-index: 10;
  padding: var(--yb-spacing-base);
  animation: fadeIn 0.2s ease-out;
  display: flex;
  flex-direction: column;
  gap: var(--yb-spacing-base);
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

.calendar-wrapper {
  position: relative;
}

.calendar-content {
  transition: filter 0.2s ease-out;
}

.calendar-content.is-loading {
  filter: blur(4px);
}

.loader-overlay {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: rgba(255, 255, 255, 0.1);
  z-index: 10;
  pointer-events: all;
}

.yb-spinner {
  color: var(--yb-color-primary);
}

.calendar-error {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: var(--yb-spacing-sm);
  padding: var(--yb-spacing-xl);
  min-height: 300px;
  text-align: center;
}

.calendar-error p {
  font-size: var(--yb-text-sm);
  color: var(--yb-color-mediumgray);
  margin: 0;
}

.calendar-error-mobile {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: var(--yb-spacing-base);
  padding: var(--yb-spacing-xl);
  min-height: 400px;
  text-align: center;
}

.calendar-error-mobile p {
  font-size: var(--yb-text-base);
  color: var(--yb-color-mediumgray);
  margin: 0;
}

.retry-button {
  margin-top: var(--yb-spacing-sm);
  padding: var(--yb-spacing-sm) var(--yb-spacing-base);
  background-color: var(--yb-color-primary);
  color: var(--yb-color-white);
  border: none;
  border-radius: var(--yb-radius-sm);
  font-size: var(--yb-text-sm);
  font-weight: var(--yb-text-semi-bold);
  cursor: pointer;
  transition: opacity 0.15s ease;

  &:hover {
    opacity: 0.9;
  }
}

.calendar-wrapper-mobile {
  position: relative;
  flex: 1;
  overflow-y: auto;
}

.loader-overlay-mobile {
  position: fixed;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10;
  pointer-events: all;
}

@media (prefers-reduced-motion: reduce) {
  .dropdown-menu {
    animation: none;
  }

  .dropdown-button svg,
  .calendar-content,
  .retry-button {
    transition: none;
  }
}

:deep(.vc-container) {
  height: 100%;
  justify-content: space-between;
}

:deep(.vc-day) {
  padding: var(--yb-spacing-xs);
}

:deep(.vc-day-content) {
  font-weight: var(--yb-text-bold);
}

:deep(.vc-day-content:hover) {
  outline: 2px solid var(--yb-calendar-primary);
}

:deep(.vc-highlight) {
  background-color: var(--yb-calendar-primary) !important;
}

:deep(.vc-highlight-content-solid) {
  color: var(--yb-calendar-text-selected) !important;
}

:deep(.vc-day .vc-disabled) {
  text-decoration-line: line-through;
  pointer-events: none;
  font-weight: var(--yb-text-semi-bold);
  opacity: 0.4;
}

:deep(.vc-arrow) {
  background-color: transparent !important;
}

:deep(.vc-title-wrapper .vc-title) {
  text-transform: capitalize;
  background: none;
  pointer-events: none;
  cursor: default;
  font-weight: var(--yb-text-bold);
}

@media (max-width: 1200px) {
  :deep(.vc-arrow) {
    display: none !important;
  }

  :deep(.vc-header) {
    margin: var(--yb-spacing-sm) 0;
  }
}
</style>
